

import React, { useState, useEffect, useMemo } from 'react';
import { Search, Trash2, X } from 'lucide-react';
import './arrondissement.css';
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import AxiosInstance from "../AxiosInstance";

const DemandesProcedure = () => {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null });
  const [successMessage, setSuccessMessage] = useState('');
  const [profil, setProfil] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const API_BASE_URL = '/api/demandes/etapes';

  // Récupérer le profil utilisateur
  useEffect(() => {
    AxiosInstance.get('/api/users/profile')
      .then(response => setProfil(response.data))
      .catch(console.error);
  }, []);

  // Redirection si utilisateur non connecté
  useEffect(() => {
    if (!isAuthenticated) navigate("/");
  }, [isAuthenticated, navigate]);

  // Récupérer les demandes
  useEffect(() => {
    const fetchDemandes = async () => {
      try {
        setLoading(true);
        const response = await AxiosInstance.get(`${API_BASE_URL}/list`);
        if (!response.ok) throw new Error("Erreur lors du chargement des demandes");

        setDemandes(response.data.data.demandes || []);
        setError('');
      } catch (err) {
        setError(err.message === 'Failed to fetch' ? "Vérifiez votre connexion Internet." : err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDemandes();
  }, []);

  // Mettre à jour le statut
  const handleStatutChange = async (id, newStatut) => {
    try {
      await AxiosInstance.patch(`${API_BASE_URL}/${id}/statut`, { statut: newStatut });

      setDemandes(prev =>
        prev.map(d => (d._id === id ? { ...d, statut: newStatut } : d))
      );
      setSuccessMessage('Statut mis à jour avec succès ✅');
    } catch (err) {
      setError(err.message);
    }
  };

  // Supprimer une demande
  const handleDelete = async (idDemande) => {
    try {
      await AxiosInstance.delete(`${API_BASE_URL}/${idDemande}`);

      setDemandes(prev => prev.filter(d => d._id !== idDemande));
      setSuccessMessage('Demande supprimée avec succès ✅');
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleteConfirm({ show: false, id: null });
    }
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case 'NON_TRAITE': return 'bg-red-200';
      case 'EN_COURS': return 'bg-yellow-200';
      case 'TRAITE': return 'bg-green-200';
      default: return 'bg-gray-200';
    }
  };

  const filteredDemandes = useMemo(() =>
    demandes.filter(d =>
      d.nomDemarche?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.phone?.toLowerCase().includes(searchTerm.toLowerCase())
    ), [demandes, searchTerm]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDemandes = filteredDemandes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredDemandes.length / itemsPerPage);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 1500);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (loading) return <div className="loading-container"><div className="loading-spinner"></div></div>;

  return (
    <div className="arrondissement-container">
      <div className="container mx-auto px-4 py-4">
        {/* Header */}
        <div className="card page-header">
          <div className="header-content">
            <div className="header-title-section">
              <h1 className="header-title">Gestion des Demandes des Étapes</h1>
              <p className="header-subtitle">Gérer les demandes des étapes</p>
            </div>
          </div>
        </div>

        {/* Barre de recherche */}
        <div className="card filters-section">
          <div className="search-container">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Rechercher une demande, description, nom, email, contact..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Tableau des demandes */}
        <div className="table-container">
          <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <label>Afficher</label>
              <select
                value={itemsPerPage}
                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
              </select>
              <span>éléments</span>
            </div>

            <div>
              {filteredDemandes.length > 0 && (
                <span>
                  Affichage {(currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, filteredDemandes.length)} sur {filteredDemandes.length}
                </span>
              )}
            </div>
          </div>
          <div className="table-scroll">
            <table className="data-table">
              <thead className="table-header">
                <tr>
                  <th>Nom de la démarche</th>
                  <th>Description</th>
                  <th>Nom</th>
                  <th>Email</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {currentDemandes.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="empty-state">
                      <p className="empty-title">Aucune demande trouvée</p>
                      <p className="empty-subtitle">Essayez de modifier vos critères de recherche</p>
                    </td>
                  </tr>
                ) : currentDemandes.map(demande => (
                  <tr key={demande._id} className="table-row">
                    <td className="table-cell">{demande.nomDemarche}</td>
                    <td className="table-cell">{demande.description || 'Aucune description'}</td>
                    <td className="table-cell">{demande.utilisateurId?.name || 'Aucun nom'}</td>
                    <td className="table-cell">{demande.utilisateurId?.email || 'Aucun email'}</td>
                    <td className={`table-cell cell-statut ${getStatutColor(demande.statut)}`}>
                      {demande.statut}
                    </td>
                    <td className="table-cell">
                      <div className="actions-container">
                        <select
                          value={demande.statut}
                          onChange={(e) => handleStatutChange(demande._id, e.target.value)}
                          className="statut-select"
                        >
                          <option value="NON_TRAITE">Non Traitée</option>
                          <option value="EN_COURS">En Cours</option>
                          <option value="TRAITE">Traitée</option>
                        </select>
                        <button
                          onClick={() => setDeleteConfirm({ show: true, id: demande._id })}
                          className="action-button delete"
                          title="Supprimer"
                        >
                          <Trash2 className="action-icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination-container">
            <button
              className="pagination-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
            >
              Précédent
            </button>

            {pageNumbers.map(number => (
              <button
                key={number}
                onClick={() => setCurrentPage(number)}
                className={`pagination-btn ${currentPage === number ? 'active' : ''}`}
              >
                {number}
              </button>
            ))}

            <button
              className="pagination-btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              Suivant
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="card stats-section">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number blue">{demandes.length}</div>
              <div className="stat-label">Total Demandes</div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {deleteConfirm.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Confirmation</h2>
              <button onClick={() => setDeleteConfirm({ show: false, id: null })}><X /></button>
            </div>
            <div className="modal-body">
              <p>Voulez-vous vraiment supprimer cette demande ?</p>
              <div className="modal-actions">
                <button className="btn btn-danger" onClick={() => setDeleteConfirm({ show: false, id: null })}>Non</button>
                <button className="btn btn-primary" onClick={() => handleDelete(deleteConfirm.id)}>Oui</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Succès</h2>
            </div>
            <div className="modal-body"><p>{successMessage}</p></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemandesProcedure;
